import { FC, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useField, useForm } from 'react-final-form';
import { useSelector } from 'react-redux';

import {
  BottomSheet,
  Option,
} from '@beeline/design-system-react/extracted-styles';
import { BottomSheetActionsType } from '@beeline/design-system-react/types/components/BottomSheet/BottomSheet.types';
import { Icons } from '@beeline/design-tokens/js/iconfont';

import { EmployeePermission } from 'src/api/permissions';
import { useHost } from 'src/providers/HostProvider';
import { getCurrentDesk, getDesksState } from 'src/store/desks/selectors';
import { unbookDesk } from 'src/store/desks/services';
import { desksActions } from 'src/store/desks/slice';
import { useAppDispatch } from 'src/store/useAppDispatch';
import { getUserPermissions, getUserState } from 'src/store/user/selectors';
import { Desk } from 'src/types';

import { DeskBookingForm } from '../DeskBookingSidesheet/DeskBookingForm';
import { DeskBookingSidesheetFields } from '../DeskBookingSidesheet/DeskBookingSidesheetFields';

import classes from './DeskBookingBottomSheet.module.scss';

interface FormBottomSheetProps {
  desk?: Desk;
  employeeNumber: string;
  permissions: Set<EmployeePermission>;
}

const Fields: FC<FormBottomSheetProps> = ({
  desk,
  employeeNumber,
  permissions,
}) => {
  const dispatch = useAppDispatch();
  const { submit, reset } = useForm();
  const { deskBookingSheetIsOpened } = useSelector(getDesksState);
  const { data } = useSelector(getUserState);

  const bookedFor = useField<Option<string> | null>('bookedFor').input.value;

  const bookable =
    !desk?.responsibleEmployeeNumber &&
    !desk?.status &&
    (!desk?.spaceId ||
      data?.spaces.has(desk.spaceId) ||
      permissions.has(EmployeePermission.BookForOthers));

  const unbookable =
    !desk?.responsibleEmployeeNumber &&
    !!desk?.status &&
    (desk.status.responsibleEmployeeNumber === employeeNumber ||
      permissions.has(EmployeePermission.EditPlans));

  const handleSetOpen = useCallback(
    (value: boolean) => {
      dispatch(desksActions.setDeskBookingSheetIsOpened(value));
      reset();
    },
    [dispatch, reset],
  );

  const book = useCallback(async () => {
    const result = await submit();
    if (!result) throw new Error('Form is invalid');

    handleSetOpen(false);
  }, [handleSetOpen, submit]);

  const unbook = useCallback(async () => {
    await dispatch(unbookDesk());

    handleSetOpen(false);
  }, [dispatch, handleSetOpen]);

  const actions = useMemo((): BottomSheetActionsType | undefined => {
    if (!permissions.has(EmployeePermission.BookDesks)) return undefined;

    if (bookable)
      return [
        {
          label:
            bookedFor && bookedFor.id !== employeeNumber
              ? 'Забронировать на коллегу'
              : 'Забронировать',
          variant: 'contained',
          onClick: book,
        },
      ];

    if (unbookable)
      return [
        {
          label: 'Отменить бронь',
          variant: 'contained',
          onClick: unbook,
        },
      ];

    return undefined;
  }, [
    book,
    bookable,
    bookedFor,
    employeeNumber,
    permissions,
    unbook,
    unbookable,
  ]);

  return (
    <BottomSheet
      isOpen={deskBookingSheetIsOpened}
      handleSetOpen={handleSetOpen}
      swiper
      modal={false}
      snapPoints={[0.5, 1]}
      className={classes.settings}
      initialSnapPoint={1}
      header={{
        icon: Icons.Pin,
        title: `Рабочее место ${desk?.title}`,
        closeButton: true,
      }}
      actions={actions}
    >
      <div className={classes.container}>
        <DeskBookingSidesheetFields
          desk={desk}
          permissions={permissions}
          bookable={bookable}
        />
      </div>
    </BottomSheet>
  );
};

export const DeskBookingBottomSheet: FC = () => {
  const { host } = useHost();
  const desk = useSelector(getCurrentDesk);
  const data = useSelector(getUserPermissions);

  if (!data?.employeeNumber || !host) return null;

  return createPortal(
    <DeskBookingForm desk={desk}>
      <Fields
        desk={desk}
        employeeNumber={data.employeeNumber}
        permissions={data.permissions}
      />
    </DeskBookingForm>,
    host,
  );
};
